import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { localizationService } from '../services/localization.service';
import {
  ACTIVE_LOCALIZATION_STATUSES,
  type LocalizationRun,
  type LocalizationSseStatus,
} from '../types/localization.types';

export const LOCALIZATION_QUERY_KEY = 'localization-run';

function isActive(run?: LocalizationRun): boolean {
  return Boolean(run && ACTIVE_LOCALIZATION_STATUSES.includes(run.status));
}

export function useLocalizationRun(runId: string | null) {
  const queryClient = useQueryClient();
  const [sseStatus, setSseStatus] = useState<LocalizationSseStatus>('disconnected');
  const query = useQuery({
    queryKey: [LOCALIZATION_QUERY_KEY, runId],
    queryFn: () => localizationService.getRun(runId!),
    enabled: Boolean(runId),
    refetchInterval: ({ state }) => isActive(state.data) ? 3000 : false,
    staleTime: 1000,
  });

  useEffect(() => {
    if (!runId || (query.data && !isActive(query.data))) {
      setSseStatus('disconnected');
      return;
    }

    const controller = new AbortController();
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const connect = async () => {
      setSseStatus('connecting');
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(localizationService.eventsUrl(runId), {
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        });
        if (!response.ok || !response.body) throw new Error(`SSE respondió ${response.status}`);
        setSseStatus('connected');
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!stopped) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split(/\r?\n\r?\n/);
          buffer = events.pop() ?? '';
          for (const event of events) {
            const data = event
              .split(/\r?\n/)
              .filter((line) => line.startsWith('data:'))
              .map((line) => line.slice(5).trimStart())
              .join('\n');
            if (!data) continue;
            const run = JSON.parse(data) as LocalizationRun;
            queryClient.setQueryData([LOCALIZATION_QUERY_KEY, runId], run);
          }
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        console.warn('Localization SSE disconnected:', error);
        setSseStatus('error');
      }
      if (!stopped && !controller.signal.aborted) {
        retryTimer = setTimeout(() => { void connect(); }, 3000);
      }
    };

    void connect();
    return () => {
      stopped = true;
      controller.abort();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [query.data?.status, queryClient, runId]);

  return { ...query, sseStatus };
}

